import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  type DragOverEvent,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { GripVertical } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useInvalidateNavLinks, useNavLinks } from '../../hooks/useNavLinks';
import { useToast } from '../../contexts/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { apiClient } from '../../services/api';
import { buildBookmarkTargetPath } from '../../utils/bookmarkUtils';
import { logger } from '../../services/logger';
import type { NavLinkScope, SavedNavLink } from '../../types';
import { BookmarkViewModal } from './BookmarkViewModal';
import { ICON_OPTIONS } from './bookmarkConstants';

// Section icons — used as visual accents for section headers
const SECTION_ICONS: Record<string, string> = {
  monitoring: '📡',
  reports: '📊',
  operations: '⚙️',
  incidents: '🚨',
  customers: '👥',
  security: '🔒',
  infrastructure: '🖥️',
  network: '🌐',
  alerts: '🔔',
  dashboards: '📈',
  compliance: '🛡️',
  assets: '📦',
  tickets: '🎫',
  configuration: '🔧',
};

function getSectionIcon(sectionName: string): string {
  const key = sectionName.toLowerCase().trim();
  for (const [keyword, emoji] of Object.entries(SECTION_ICONS)) {
    if (key.includes(keyword)) return emoji;
  }
  return '📂';
}

const UNGROUPED_SECTION_KEY = '__ungrouped__';
const SECTION_DROP_ZONE_ID_PREFIX = 'section-drop:';

function toSectionKey(sectionName: string | null | undefined): string {
  return sectionName ? sectionName : UNGROUPED_SECTION_KEY;
}

function fromSectionKey(sectionKey: string): string | null {
  return sectionKey === UNGROUPED_SECTION_KEY ? null : sectionKey;
}

function toSectionDropZoneId(sectionName: string | null): string {
  return `${SECTION_DROP_ZONE_ID_PREFIX}${toSectionKey(sectionName)}`;
}

function resolveSectionKeyFromOverId(overId: string, links: SavedNavLink[]): string | null {
  if (overId.startsWith(SECTION_DROP_ZONE_ID_PREFIX)) {
    return overId.slice(SECTION_DROP_ZONE_ID_PREFIX.length);
  }

  const overLink = links.find((link) => link.id === overId);
  if (!overLink) return null;
  return toSectionKey(overLink.sectionName);
}

function buildGroupedLinks(inputLinks: SavedNavLink[]): { section: string | null; items: SavedNavLink[] }[] {
  const groups: { section: string | null; items: SavedNavLink[] }[] = [];
  const sectionMap = new Map<string | null, SavedNavLink[]>();

  for (const link of inputLinks) {
    const key = link.sectionName || null;
    if (!sectionMap.has(key)) sectionMap.set(key, []);
    sectionMap.get(key)?.push(link);
  }

  // Sort bookmarks alphabetically by name
  const sortLinksAlphabetically = (a: SavedNavLink, b: SavedNavLink) => {
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  };

  // Add ungrouped section first (if exists)
  const ungrouped = sectionMap.get(null);
  if (ungrouped) {
    groups.push({ section: null, items: [...ungrouped].sort(sortLinksAlphabetically) });
  }

  // Get named sections and sort them alphabetically
  const sectionEntries = [...sectionMap.entries()].filter(
    (entry): entry is [string, SavedNavLink[]] => entry[0] !== null,
  );

  sectionEntries.sort(([sectionA], [sectionB]) => {
    return sectionA.localeCompare(sectionB, undefined, { sensitivity: 'base' });
  });

  // Add sections with alphabetically sorted bookmarks
  for (const [section, items] of sectionEntries) {
    groups.push({ section, items: [...items].sort(sortLinksAlphabetically) });
  }

  return groups;
}

function DraggableBookmarkRow({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled: boolean;
  children: (dragHandleProps: { 
    attributes: ReturnType<typeof useDraggable>['attributes']; 
    listeners: ReturnType<typeof useDraggable>['listeners'];
  }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });

  return (
    <div ref={setNodeRef} className={isDragging ? 'opacity-30' : undefined}>
      {children({ attributes, listeners })}
    </div>
  );
}

function SectionDropContainer({
  sectionName,
  className,
  children,
}: {
  sectionName: string | null;
  className: string;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: toSectionDropZoneId(sectionName) });
  return (
    <div ref={setNodeRef} className={className}>
      {children}
    </div>
  );
}

interface NavLinkMenuProps {
  scope: NavLinkScope;
  icon: React.ReactNode;
  label?: string;
  emptyMessage: string;
}

const EMPTY_LINKS: SavedNavLink[] = [];

export function NavLinkMenu({ scope, icon, label, emptyMessage }: NavLinkMenuProps) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const invalidateNavLinks = useInvalidateNavLinks();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: fetchedLinks = EMPTY_LINKS,
    isLoading,
    isError,
    error,
  } = useNavLinks(scope, isOpen);
  const [links, setLinks] = useState<SavedNavLink[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isManaging, setIsManaging] = useState(false);
  const [editingBookmark, setEditingBookmark] = useState<SavedNavLink | undefined>(undefined);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editingSectionName, setEditingSectionName] = useState('');
  const [editingSectionIcon, setEditingSectionIcon] = useState('');
  const [showSectionIconPicker, setShowSectionIconPicker] = useState(false);
  const [activeBookmarkId, setActiveBookmarkId] = useState<string | null>(null);
  const [activeDropSectionKey, setActiveDropSectionKey] = useState<string | null>(null);

  const canManageTenant = usePermission('nav_links_tenant.manage');
  const canManageSystem = usePermission('nav_links_system.manage');
  const canManage =
    scope === 'User' ||
    (scope === 'Tenant' && canManageTenant) ||
    (scope === 'System' && canManageSystem);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  useEffect(() => {
    logger.debug('NavLinks loaded from query', { scope, count: fetchedLinks.length });
    setLinks(fetchedLinks);
  }, [fetchedLinks, scope]);

  useEffect(() => {
    if (!isError) return;
    const status = (error as { response?: { status?: number } } | undefined)?.response?.status;
    logger.error('Failed to fetch nav links', { scope, status, error: String(error) });
  }, [error, isError, scope]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsManaging(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setIsManaging(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const existingSections = useMemo(
    () => [...new Set(links.map((l) => l.sectionName).filter(Boolean) as string[])].sort(),
    [links],
  );

  const filteredLinks = useMemo(() => {
    if (!searchQuery.trim()) return links;
    const q = searchQuery.toLowerCase();
    return links.filter(
      (link) =>
        link.name.toLowerCase().includes(q) ||
        link.sectionName?.toLowerCase().includes(q) ||
        link.targetPath.toLowerCase().includes(q),
    );
  }, [links, searchQuery]);

  const groupedLinks = useMemo(() => buildGroupedLinks(filteredLinks), [filteredLinks]);
  const activeBookmark = useMemo(
    () => (activeBookmarkId ? links.find((link) => link.id === activeBookmarkId) ?? null : null),
    [activeBookmarkId, links],
  );

  const handleNavigate = (link: SavedNavLink) => {
    setIsOpen(false);
    // Use bookmark ID in URL instead of full query string
    const url = `${link.targetPath}${link.targetPath.includes('?') ? '&' : '?'}bookmarkId=${link.id}`;
    navigate(url);
  };

  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const handleDelete = async (id: string) => {
    if (!canManage || !user?.tenantId || deletingIds.has(id)) return;

    const linkName = links.find((l) => l.id === id)?.name;
    const previousLinks = [...links];
    logger.info('Deleting nav link', { scope, id, name: linkName });

    setDeletingIds((prev) => new Set(prev).add(id));
    setLinks((prev) => prev.filter((link) => link.id !== id));

    try {
      await apiClient.deleteNavLink(user.tenantId, id);
      logger.info('Nav link deleted', { scope, id, name: linkName });
      addToast('Bookmark deleted', 'success');
      invalidateNavLinks(scope);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      logger.error('Failed to delete nav link', { scope, id, name: linkName, status, detail, error: String(err) });
      addToast(detail || `Failed to delete bookmark (${status || 'unknown error'})`, 'error');
      setLinks(previousLinks);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleUpdateToCurrentView = async (link: SavedNavLink) => {
    if (!user?.tenantId) return;

    logger.info('Updating nav link to current view', { scope, id: link.id, name: link.name, newPath: buildBookmarkTargetPath(location.pathname, location.search) });

    try {
      const params = new URLSearchParams(location.search);
      await apiClient.updateNavLink(user.tenantId, link.id, {
        ...link,
        id: link.id,
        targetPath: buildBookmarkTargetPath(location.pathname, location.search),
        filterConfigJson: params.get('filters') || undefined,
        columnConfigJson: params.get('columns') || undefined,
        sortConfigJson: params.get('sortBy') || undefined,
        canBeModifiedByOthers: link.canBeModifiedByOthers,
      });
      logger.info('Nav link updated to current view', { scope, id: link.id, name: link.name });
      addToast('Bookmark updated to current view', 'success');
      invalidateNavLinks(scope);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      logger.error('Failed to update nav link', { scope, id: link.id, name: link.name, status, detail, error: String(err) });
      addToast(detail || `Failed to update bookmark (${status || 'unknown error'})`, 'error');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    // Only handle bookmark dragging now (no section dragging)
    if (!activeId.startsWith('section:')) {
      setActiveBookmarkId(activeId);
      setActiveDropSectionKey(null);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (!event.over) {
      setActiveDropSectionKey(null);
      return;
    }
    
    const activeId = String(event.active.id);
    const overId = String(event.over.id);
    
    // Only handle bookmark dragging
    if (!activeId.startsWith('section:')) {
      if (overId.startsWith('section:')) {
        setActiveDropSectionKey(overId.replace('section:', ''));
      } else {
        const nextSectionKey = resolveSectionKeyFromOverId(overId, links);
        setActiveDropSectionKey(nextSectionKey);
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!canManage || !user?.tenantId) {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
      return;
    }

    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);

    // Only handle bookmark moves (no section reordering)
    if (activeId.startsWith('section:')) {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
      return;
    }

    const sourceLink = links.find((item) => item.id === activeId);
    if (!sourceLink) {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
      return;
    }

    const targetSectionKey = resolveSectionKeyFromOverId(overId, links);
    if (!targetSectionKey) {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
      return;
    }

    const targetSectionName = fromSectionKey(targetSectionKey);
    const sourceSectionKey = toSectionKey(sourceLink.sectionName);
    
    // Only update if moving to a different section
    if (sourceSectionKey === targetSectionKey) {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
      return;
    }

    const previousLinks = [...links];

    // Optimistically update the section assignment
    const updatedLinks = links.map((link) =>
      link.id === activeId ? { ...link, sectionName: targetSectionName ?? undefined } : link
    );
    setLinks(updatedLinks);

    try {
      await apiClient.updateNavLink(user.tenantId, sourceLink.id, {
        id: sourceLink.id,
        tenantId: sourceLink.tenantId,
        userId: sourceLink.userId,
        scope: sourceLink.scope,
        name: sourceLink.name,
        description: sourceLink.description,
        icon: sourceLink.icon,
        sectionName: targetSectionName ?? undefined,
        sectionIcon: sourceLink.sectionIcon,
        targetPath: sourceLink.targetPath,
        filterConfigJson: sourceLink.filterConfigJson,
        columnConfigJson: sourceLink.columnConfigJson,
        sortConfigJson: sourceLink.sortConfigJson,
        requiredPermission: sourceLink.requiredPermission,
        canBeModifiedByOthers: sourceLink.canBeModifiedByOthers,
      });
      
      logger.info('Bookmark moved to different section', {
        scope,
        bookmarkId: activeId,
        fromSection: sourceLink.sectionName,
        toSection: targetSectionName,
      });
      addToast(`Bookmark moved to ${targetSectionName || 'Ungrouped'}`, 'success');
      invalidateNavLinks(scope);
    } catch (err: unknown) {
      logger.error('Failed to move bookmark to section', {
        scope,
        activeId,
        targetSectionName,
        error: String(err),
      });
      addToast('Failed to move bookmark', 'error');
      setLinks(previousLinks);
      invalidateNavLinks(scope);
    } finally {
      setActiveBookmarkId(null);
      setActiveDropSectionKey(null);
    }
  };

  const startSectionEdit = (sectionName: string, sectionItems: SavedNavLink[]) => {
    setEditingSection(sectionName);
    setEditingSectionName(sectionName);
    setEditingSectionIcon(sectionItems[0]?.sectionIcon || '');
    setShowSectionIconPicker(false);
  };

  const cancelSectionEdit = () => {
    setEditingSection(null);
    setEditingSectionName('');
    setEditingSectionIcon('');
    setShowSectionIconPicker(false);
  };

  const handleSaveSection = async () => {
    if (!user?.tenantId || !editingSection) return;

    const nextSectionName = editingSectionName.trim();
    if (!nextSectionName) {
      addToast('Section name is required', 'error');
      return;
    }

    const nextSectionIcon = editingSectionIcon.trim() || undefined;
    const sectionLinks = links.filter((link) => link.sectionName === editingSection);
    if (sectionLinks.length === 0) {
      cancelSectionEdit();
      return;
    }

    const previousLinks = [...links];

    setLinks((prev) =>
      prev.map((link) =>
        link.sectionName === editingSection
          ? { ...link, sectionName: nextSectionName, sectionIcon: nextSectionIcon }
          : link,
      ),
    );

    try {
      await Promise.all(
        sectionLinks.map((link) =>
          apiClient.updateNavLink(user.tenantId!, link.id, {
            id: link.id,
            tenantId: link.tenantId,
            userId: link.userId,
            scope: link.scope,
            name: link.name,
            description: link.description,
            icon: link.icon,
            sectionName: nextSectionName,
            sectionIcon: nextSectionIcon,
            targetPath: link.targetPath,
             filterConfigJson: link.filterConfigJson,
             columnConfigJson: link.columnConfigJson,
             sortConfigJson: link.sortConfigJson,
             requiredPermission: link.requiredPermission,
             canBeModifiedByOthers: link.canBeModifiedByOthers,
           }),
        ),
      );
      logger.info('Section updated', {
        scope,
        previousSectionName: editingSection,
        nextSectionName,
        count: sectionLinks.length,
      });
      addToast('Section updated', 'success');
      invalidateNavLinks(scope);
      cancelSectionEdit();
    } catch (err: unknown) {
      logger.error('Failed to update section', {
        scope,
        previousSectionName: editingSection,
        nextSectionName,
        error: String(err),
      });
      setLinks(previousLinks);
      addToast('Failed to update section', 'error');
      invalidateNavLinks(scope);
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => {
          setIsOpen((prev) => !prev);
          setSearchQuery('');
          setIsManaging(false);
        }}
        className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
        title={label || 'Navigation links'}
      >
        {icon}
        {label ? <span className="hidden lg:inline">{label}</span> : null}
      </button>

      {isOpen && (
        <div className="absolute left-0 z-50 mt-2 flex max-h-[75vh] w-96 flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10">
          {/* Search */}
          <div className="border-b border-gray-200 p-2 dark:border-gray-700">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              autoFocus
            />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div role="status" aria-live="polite" className="px-4 py-8 text-center text-sm text-gray-500">Loading...</div>
            )}

            {!isLoading && isError && (
              <div role="alert" className="px-4 py-8 text-center text-sm text-red-700 dark:text-red-300">
                Failed to load links. Close and reopen the menu to try again.
              </div>
            )}

            {!isLoading && !isError && filteredLinks.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                {searchQuery ? 'No matches found' : emptyMessage}
              </div>
            )}

            {!isLoading && !isError && filteredLinks.length > 0 && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={(event) => void handleDragEnd(event)}
                onDragCancel={() => {
                  setActiveBookmarkId(null);
                  setActiveDropSectionKey(null);
                }}
              >
                {groupedLinks.map((group, gIdx) => {
                  const sectionKey = toSectionKey(group.section);
                  const isDropTarget = activeDropSectionKey === sectionKey;
                  const showDropHighlight = isManaging && canManage && !!activeBookmarkId;

                  return (
                      <div key={group.section ?? UNGROUPED_SECTION_KEY}>
                        {group.section && (
                          <>
                            <div
                              className={`flex items-center gap-2 bg-gray-50 px-4 py-2 dark:bg-gray-900/60 ${
                                gIdx > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''
                              }`}
                            >
                              <span className="text-sm">
                                {group.items.find((item) => item.sectionIcon)?.sectionIcon ||
                                  (group.section ? getSectionIcon(group.section) : '')}
                              </span>
                              <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                {group.section}
                              </span>
                              {isManaging && canManage && (
                                <button
                                  onClick={() => startSectionEdit(group.section!, group.items)}
                                  className="ml-auto rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700"
                                  title="Edit section"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                  </svg>
                                </button>
                              )}
                            </div>
                            {isManaging && canManage && editingSection === group.section && (
                              <div className="space-y-2 border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/40">
                                <div>
                                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Section name
                                  </label>
                                  <input
                                    type="text"
                                    value={editingSectionName}
                                    onChange={(e) => setEditingSectionName(e.target.value)}
                                    maxLength={100}
                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                    Section icon
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setShowSectionIconPicker((prev) => !prev)}
                                    className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                                  >
                                    <span className="text-base leading-none">
                                      {editingSectionIcon || getSectionIcon(group.section)}
                                    </span>
                                    <span className="text-gray-500 dark:text-gray-400">
                                      {editingSectionIcon ? 'Change section icon' : 'Choose section icon'}
                                    </span>
                                  </button>
                                  {showSectionIconPicker && (
                                    <div className="mt-2 grid grid-cols-10 gap-1 rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-700">
                                      {ICON_OPTIONS.map((emoji) => (
                                        <button
                                          key={`section-icon-${emoji}`}
                                          type="button"
                                          onClick={() => {
                                            setEditingSectionIcon(emoji);
                                            setShowSectionIconPicker(false);
                                          }}
                                          className={`flex h-7 w-7 items-center justify-center rounded text-sm transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/30 ${
                                            editingSectionIcon === emoji ? 'bg-blue-200 ring-2 ring-blue-500 dark:bg-blue-800' : ''
                                          }`}
                                        >
                                          {emoji}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={cancelSectionEdit}
                                    className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleSaveSection}
                                    disabled={!editingSectionName.trim()}
                                    className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Save section
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        <SectionDropContainer
                          sectionName={group.section}
                          className={`transition-all ${
                            showDropHighlight
                              ? isDropTarget
                                ? 'bg-blue-50/70 ring-2 ring-inset ring-blue-300 dark:bg-blue-900/20 dark:ring-blue-700'
                                : 'ring-1 ring-inset ring-transparent'
                              : ''
                          }`}
                        >
                          {group.items.map((link) => (
                            <DraggableBookmarkRow
                              key={link.id}
                              id={link.id}
                              disabled={!isManaging || !canManage}
                            >
                              {({ attributes, listeners }) => (
                                <div className="group flex items-center pl-4">
                                  {isManaging && canManage && (
                                    <button
                                      {...attributes}
                                      {...listeners}
                                      className="cursor-grab active:cursor-grabbing shrink-0 rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                                      title="Drag to move to another section"
                                      aria-label="Drag to move to another section"
                                    >
                                      <GripVertical className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleNavigate(link)}
                                    className="flex flex-1 items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-blue-50/60 dark:hover:bg-gray-700/50"
                                  >
                                    <span className="shrink-0 text-base leading-none">
                                      {link.icon || '📄'}
                                    </span>
                                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {link.name}
                                    </span>
                                  </button>

                                  {isManaging && canManage && (
                                    <div className="flex shrink-0 items-center gap-0.5 pr-2">
                                      {/* Update to current view */}
                                      <button
                                        onClick={() => handleUpdateToCurrentView(link)}
                                        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20"
                                        title="Update to current view"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                                        </svg>
                                      </button>
                                      {/* Edit */}
                                      <button
                                        onClick={() => {
                                          setEditingBookmark(link);
                                          setIsOpen(false);
                                        }}
                                        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
                                        title="Edit"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                        </svg>
                                      </button>
                                      {/* Delete */}
                                      <button
                                        onClick={() => handleDelete(link.id)}
                                        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                                        title="Delete"
                                      >
                                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                        </svg>
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </DraggableBookmarkRow>
                          ))}
                        </SectionDropContainer>
                      </div>
                    );
                  })}

                <DragOverlay>
                  {activeBookmark ? (
                    <div className="flex w-80 items-center gap-3 rounded-lg border border-blue-300 bg-white/95 px-4 py-2.5 text-sm shadow-xl dark:border-blue-700 dark:bg-gray-800/95">
                      <span className="shrink-0 text-base leading-none">{activeBookmark.icon || '📄'}</span>
                      <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-gray-100">
                        {activeBookmark.name}
                      </span>
                      <span className="text-xs text-blue-600 dark:text-blue-300">Moving</span>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>

          {/* Footer */}
          {canManage && links.length > 0 && (
            <div className="border-t border-gray-200 p-2 dark:border-gray-700">
              <button
                onClick={() => setIsManaging((prev) => !prev)}
                className="w-full rounded-lg px-3 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                {isManaging ? '✓ Done managing' : '⚙ Manage bookmarks'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit bookmark modal */}
      <BookmarkViewModal
        isOpen={!!editingBookmark}
        onClose={() => setEditingBookmark(undefined)}
        currentPath={editingBookmark?.targetPath ?? location.pathname}
        existingBookmark={editingBookmark}
        existingSections={existingSections}
      />
    </div>
  );
}
